import {useEffect} from "react";
import {storyblokInit, apiPlugin} from "@storyblok/react";
import {Outlet, ScrollRestoration, useLocation} from "react-router-dom";
import storyblokConfig from "../dev/scripts/storyblokConfig";
import RefUseStoryblok from "../dev/scripts/refUseStoryblok";

import ThemeProvider from "./context/themecontext";

import {GetWindowLanguage} from "./helpers/storyHelpers";
import {configureApi} from "./api/utils/configureApi";

/// >> Bloks
// Panels
import AccordionPanel from "./bloks/panels/accordionPanel/accordionPanel";
import ArticleTypeSummary from "./bloks/panels/articleTypeSummary/articleTypeSummary";
import AuthorDetailPanel from "./bloks/panels/authorDetailPanel/authorDetailPanel";
import CustomTagPanel from "./bloks/panels/customTagPanel/customTagPanel";
import GridPanel from "./bloks/panels/gridPanel/gridPanel";
import MastheadPanel from "./bloks/panels/mastheadPanel/mastheadPanel";
import RichTextPanel from "./bloks/panels/richTextPanel/richTextPanel";
import SeriesSliderPanel from "./bloks/panels/seriesSliderPanel/seriesSliderPanel";
import TablePanel from "./bloks/table/tablePanel";
import ArticleListingPanel from "./bloks/panels/articleListingPanel/articleListingPanel";
import ThreeColumnLayoutPanel from "./bloks/panels/threeColumnLayoutPanel/threeColumnLayoutPanel";
import TwoColumn5050ListingPanel from "./bloks/panels/articleListingPanel/TwoColumn5050ListingPanel";
import ThreeColumnSMLListingPanel from "./bloks/panels/articleListingPanel/ThreeColumnSMLListingPanel";
import ThreeColumnLSMListingPanel from "./bloks/panels/articleListingPanel/ThreeColumnLSMListingPanel";
import CuratedProductsListingPanel from "./bloks/panels/curatedProductsListingPanel/curatedProductsListingPanel";
import RecommendedProductsListingPanel from "./bloks/panels/recommendedProductsListingPanel/recommendedProductsListingPanel";
import TopStoriesPanel from "./bloks/panels/topStoriesPanel/topStoriesPanel";
import HeaderBannerPanel from "./bloks/panels/headerBannerPanel/headerBannerPanel";
import LowerBannerPanel from "./bloks/panels/lowerBannerPanel/lowerBannerPanel";

// Aside Widgets
import ArticleListingCuratedWidget from "./bloks/asidewidgets/articleListingCuratedWidget/articleListingCuratedWidget";
import ArticleListingByCategoryWidget from "./bloks/asidewidgets/articleListingByCategoryWidget/articleListingByCategoryWidget";
import SeriesListingWidget from "./bloks/asidewidgets/seriesListingWidget/seriesListingWidget";
import LatestNewsWidget from "./bloks/asidewidgets/latestNewsWidget/latestNewsWidget";
import LatestNewsFeaturedWidget from "./bloks/asidewidgets/latestNewsFeaturedWidget/latestNewsFeaturedWidget";
import TopStoriesAsideWidget from "./bloks/asidewidgets/topStoriesAsideWidget/topStoriesAsideWidget";
import ArticleTypeFeaturedWidget from "./bloks/asidewidgets/articleTypeFeaturedWidget/articleTypeFeaturedWidget";
import ArticleListingByArticleTypeWidget from "./bloks/asidewidgets/articleListingByArticleTypeWidget/articleListingByArticleTypeWidget";
// Article Panels
import ColumnsSimpleWidget from "./bloks/articlepanels/columnsSimpleWidget/columnsSimpleWidget";
import html5audioembed from "./bloks/articlepanels/html5audioembed/html5audioembed";
import html5videoembed from "./bloks/articlepanels/html5videoembed/html5videoembed";
import HTMLEmbed from "./bloks/articlepanels/htmlEmbed/htmlEmbed";
import IframeEmbed from "./bloks/articlepanels/iframeembed/iframeembed";
import Image from "./bloks/articlepanels/image/image";
import instagramembed from "./bloks/articlepanels/instagramembed/instagramembed";
import RichText from "./bloks/articlepanels/richText/richText";
import RiddleEmbed from "./bloks/articlepanels/riddleembed/riddleembed";
import tiktokembed from "./bloks/articlepanels/tiktokembed/tiktokembed";
import twitterembed from "./bloks/articlepanels/twitterembed/twitterembed";
import youtubeembed from "./bloks/articlepanels/youtubeembed/youtubeembed";
import ArticleBannerPanel from "./bloks/articlepanels/articleBannerPanel/articleBannerPanel";
import SeasonalSeriesPanel from "./bloks/articlepanels/seasonalseriespanel/seasonalSeriesPanel";

//Category Panels
import CategoryHeaderPanel from "./bloks/categorypanels/categoryheaderpanel/categoryheaderpanel";

/// Widgets
import AccordionItem from "./bloks/accordion/accordionItem";
import TextAndLinkCard from "./components/textAndLinkCard/textAndLinkCard";
import LeftNavItem from "./components/leftNav/leftNavItem";
import LanguageProvider from "./context/languagecontext";
import StoreProduct from "./bloks/storeProduct/storeProduct";
import AuthorCard from "./bloks/authorCard/authorCard";
import SeasonalSeriesCard from "./components/seasonalSeriesCard/seasonalSeriesCard";

/// Rich Text Widgets
import RichTextAnchor from "./bloks/richTextWidgets/richTextAnchor/richTextAnchor";
import RichTextDataDefinitionList from "./bloks/richTextWidgets/richTextDataDefinition/richTextDataDefinitionList";
import RichTextDataDefinitionListItem from "./bloks/richTextWidgets/richTextDataDefinition/richTextDataDefinitionListItem";
import RichTextSummaryDetail from "./bloks/richTextWidgets/richTextSummaryDetail/richTextSummaryDetail";
import RichTextIndention from "./bloks/richTextIndention/richTextIndention";
import SpotlightCarousel from "./bloks/homepage/spotlightCarousel/spotlightCarousel";
import DataDefinitionPanel from "./bloks/articlepanels/dataDefinitionPanel/dataDefinitionPanel";
import SummaryDetailPanel from "./bloks/articlepanels/summaryDetailPanel/summaryDetailPanel";
import AnchorPanel from "./bloks/articlepanels/anchorPanel/anchorPanel";
import EditorsChoiceCarousel from "./bloks/homepage/editorsChoiceCarousel/editorsChoiceCarousel";
import StylesLoader from "./components/StylesLoader";
import Evidon from "./features/evidon/components/Evidon";
import DocumentDir from "./features/localization/components/documentDir";
import {FlashMessageContainer} from "./features/flashMessage/flashMessageContainer";
import Shell from "./components/shell";
import AuthInterruptModal from "./features/modals/components/AuthInterruptModal";
import EmailRestrictedModal from "./features/modals/components/EmailRestrictedModal";
import UsernameRestrictedModal from "./features/modals/components/UsernameRestrictedModal";
import {useIsNotFound} from "./features/notFound/store";
import AppAnalytics from "./components/AppAnalytics/AppAnalytics";

storyblokInit({
  accessToken: storyblokConfig.devToken,
  use: [apiPlugin],
  components: {
    // Panels
    articletypesummary: ArticleTypeSummary,
    accordionpanel: AccordionPanel,
    authordetailpanel: AuthorDetailPanel,
    curatedproductslistingpanel: CuratedProductsListingPanel,
    customizable_tag_panel: CustomTagPanel,
    gridpanel: GridPanel,
    mastheadpanel: MastheadPanel,
    recommendedproductslistingpanel: RecommendedProductsListingPanel,
    richtextpanel: RichTextPanel,
    seriessliderpanel: SeriesSliderPanel,
    tablepanel: TablePanel,
    articlelistingpanel: ArticleListingPanel,
    three_column_layout_panel: ThreeColumnLayoutPanel,
    twocolumnevenarticlelistingpanel: TwoColumn5050ListingPanel,
    threecolumnsmlarticlelistingpanel: ThreeColumnSMLListingPanel,
    threecolumnlsmarticlelistingpanel: ThreeColumnLSMListingPanel,
    topstoriespanel: TopStoriesPanel,
    header_banner_panel: HeaderBannerPanel,
    lower_banner_panel: LowerBannerPanel,
    // Aside Widgets
    articlelistingcuratedwidget: ArticleListingCuratedWidget,
    articlelistingbycategorywidget: ArticleListingByCategoryWidget,
    articlelistingbyarticletypewidget: ArticleListingByArticleTypeWidget,
    article_type_featured_aside_widget: ArticleTypeFeaturedWidget,
    latestnewsasidewidget: LatestNewsWidget,
    latest_news_featured_aside_widget: LatestNewsFeaturedWidget,
    serieslistingwidget: SeriesListingWidget,
    topstoriesasidewidget: TopStoriesAsideWidget,
    // Article Panels
    columnswidget: ColumnsSimpleWidget,
    datadefinitionpanel: DataDefinitionPanel,
    summarydetailpanel: SummaryDetailPanel,
    anchorpanel: AnchorPanel,
    html5audioembed: html5audioembed,
    html5videoembed: html5videoembed,
    htmlembed: HTMLEmbed,
    iframeembed: IframeEmbed,
    image: Image,
    instagramembed: instagramembed,
    richtext: RichText,
    riddleembed: RiddleEmbed,
    tiktokembed: tiktokembed,
    twitterembed: twitterembed,
    youtubeembed: youtubeembed,
    article_banner_panel: ArticleBannerPanel,
    seasonalseriespanel: SeasonalSeriesPanel,
    //Category Panels
    categoryheaderpanel: CategoryHeaderPanel,
    //Widgets
    accordionitem: AccordionItem,
    authorcard: AuthorCard,
    storeproduct: StoreProduct,
    richtextindention: RichTextIndention,
    richtextindentionpanel: RichTextIndention,
    textandlinkcard: TextAndLinkCard,
    seasonalseriescard: SeasonalSeriesCard,
    //Home Page
    spotlightcarousel: SpotlightCarousel,
    editorschoicecarousel: EditorsChoiceCarousel,
    //Rich Text Widgets
    anchor: RichTextAnchor,
    datadefinitionlist: RichTextDataDefinitionList,
    datadefinitionlistitem: RichTextDataDefinitionListItem,
    summarydetail: RichTextSummaryDetail,
    //Navigation Widgets
    leftnavitem: LeftNavItem,
  },
});

function App() {
  const {pathname} = useLocation();
  const lang = GetWindowLanguage();

  useEffect(() => {
    configureApi({lang});
  }, [lang]);

  const {isNotFound} = useIsNotFound();

  const slug = (lang ? pathname.split(`/${lang}/`).pop() : pathname).replace(
    /(.+)\/+$/,
    "$1"
  );

  const story = RefUseStoryblok(slug);

  return (
    <>
      <AppAnalytics />
      <DocumentDir />
      <Evidon />
      <ThemeProvider>
        <StylesLoader />
        <FlashMessageContainer />
        <AuthInterruptModal />
        <EmailRestrictedModal />
        <UsernameRestrictedModal />
        <LanguageProvider defaultLanguage={story.lang || "en"}>
          {story?.content ? (
            <Outlet context={{story}} />
          ) : (
            <Shell isNotFound={isNotFound} />
          )}
          <ScrollRestoration />
        </LanguageProvider>
      </ThemeProvider>
    </>
  );
}

export default App;
